import type { Translations } from "../language";

/**
 * The words of the visitor's generated name (B3.1; L10N2: in every language the game speaks), a
 * mood and a noun, "Grumpy Badger", "Ворчливый Барсук", "Tejón Gruñón". The game is about
 * grudges, so the first moods are sulks, then the bold and the bedraggled; the nouns are the
 * realm's folk, its beasts and what is in its larder. Each language has its own words, in the
 * same order of kinds, 64 by 64: two names in a duel match once in 4096 (`src/front/name.ts`).
 *
 * A mood agrees with its noun where the language asks it to: an entry is one word, or its
 * masculine and feminine forms as "Ворчливый|Ворчливая", and the nouns come by gender. Every
 * combination fits `nameMaxLength` (16): the longest mood form of a gender, a space and the
 * longest noun of that gender (`test/frontName.test.ts`).
 */
export interface NameWords {
  /** The mood before the noun ("Grumpy Badger"), else after it ("Tejón Gruñón"). */
  readonly moodFirst: boolean;
  readonly moods: readonly string[];
  readonly masculine: readonly string[];
  readonly feminine: readonly string[];
}

const en: NameWords = {
  moodFirst: true,
  moods: [
    "Grumpy",
    "Sulky",
    "Petty",
    "Salty",
    "Bitter",
    "Cranky",
    "Huffy",
    "Miffed",
    "Peeved",
    "Testy",
    "Surly",
    "Sour",
    "Snippy",
    "Prickly",
    "Moody",
    "Gloomy",
    "Glum",
    "Dour",
    "Touchy",
    "Vexed",
    "Fuming",
    "Grouchy",
    "Crabby",
    "Tetchy",
    "Ornery",
    "Grumbly",
    "Smug",
    "Snooty",
    "Haughty",
    "Pompous",
    "Sneaky",
    "Shifty",
    "Wily",
    "Crafty",
    "Feisty",
    "Fierce",
    "Bold",
    "Gallant",
    "Valiant",
    "Stout",
    "Plucky",
    "Grim",
    "Stern",
    "Rowdy",
    "Unruly",
    "Jolly",
    "Cheeky",
    "Fiery",
    "Soggy",
    "Muddy",
    "Rusty",
    "Mossy",
    "Scruffy",
    "Wobbly",
    "Clumsy",
    "Sleepy",
    "Greedy",
    "Stingy",
    "Hapless",
    "Dashing",
    "Mighty",
    "Burly",
    "Gruff",
    "Doughty",
  ],
  masculine: [
    "Baron",
    "Squire",
    "Friar",
    "Jester",
    "Miller",
    "Tinker",
    "Cobbler",
    "Peasant",
    "Knight",
    "Herald",
    "Bard",
    "Monk",
    "Abbot",
    "Duke",
    "Yeoman",
    "Sheriff",
    "Hermit",
    "Pilgrim",
    "Goatherd",
    "Fletcher",
    "Steward",
    "Varlet",
    "Badger",
    "Goose",
    "Toad",
    "Weasel",
    "Ferret",
    "Mule",
    "Goat",
    "Boar",
    "Crow",
    "Raven",
    "Mole",
    "Otter",
    "Stoat",
    "Hedgehog",
    "Rooster",
    "Donkey",
    "Hound",
    "Magpie",
    "Heron",
    "Walrus",
    "Snail",
    "Puffin",
    "Turnip",
    "Cabbage",
    "Parsnip",
    "Radish",
    "Pudding",
    "Dumpling",
    "Muffin",
    "Pickle",
    "Herring",
    "Mutton",
    "Porridge",
    "Sausage",
    "Leek",
    "Teapot",
    "Kettle",
    "Bucket",
    "Ladle",
    "Anvil",
    "Barrel",
    "Gargoyle",
  ],
  feminine: [],
};

export const NAME_WORDS: Translations<NameWords> = {
  en,
  ru: {
    moodFirst: true,
    moods: [
      "Ворчливый|Ворчливая",
      "Надутый|Надутая",
      "Мелочный|Мелочная",
      "Солёный|Солёная",
      "Желчный|Желчная",
      "Сварливый|Сварливая",
      "Обидчивый|Обидчивая",
      "Обиженный|Обиженная",
      "Злющий|Злющая",
      "Нервный|Нервная",
      "Угрюмый|Угрюмая",
      "Кислый|Кислая",
      "Колкий|Колкая",
      "Колючий|Колючая",
      "Капризный|Капризная",
      "Мрачный|Мрачная",
      "Унылый|Унылая",
      "Хмурый|Хмурая",
      "Ранимый|Ранимая",
      "Злой|Злая",
      "Бешеный|Бешеная",
      "Сердитый|Сердитая",
      "Вредный|Вредная",
      "Дёрганый|Дёрганая",
      "Упрямый|Упрямая",
      "Бурчащий|Бурчащая",
      "Довольный|Довольная",
      "Чванный|Чванная",
      "Надменный|Надменная",
      "Важный|Важная",
      "Ушлый|Ушлая",
      "Скользкий|Скользкая",
      "Лукавый|Лукавая",
      "Хитрый|Хитрая",
      "Бойкий|Бойкая",
      "Свирепый|Свирепая",
      "Смелый|Смелая",
      "Галантный|Галантная",
      "Храбрый|Храбрая",
      "Дюжий|Дюжая",
      "Удалой|Удалая",
      "Суровый|Суровая",
      "Строгий|Строгая",
      "Буйный|Буйная",
      "Шальной|Шальная",
      "Весёлый|Весёлая",
      "Дерзкий|Дерзкая",
      "Огненный|Огненная",
      "Мокрый|Мокрая",
      "Грязный|Грязная",
      "Ржавый|Ржавая",
      "Мшистый|Мшистая",
      "Лохматый|Лохматая",
      "Шаткий|Шаткая",
      "Неуклюжий|Неуклюжая",
      "Сонный|Сонная",
      "Жадный|Жадная",
      "Скупой|Скупая",
      "Невезучий|Невезучая",
      "Лихой|Лихая",
      "Могучий|Могучая",
      "Плечистый|Плечистая",
      "Грубый|Грубая",
      "Бравый|Бравая",
    ],
    masculine: [
      "Барон",
      "Сквайр",
      "Чернец",
      "Шут",
      "Медник",
      "Мужик",
      "Рыцарь",
      "Бард",
      "Монах",
      "Аббат",
      "Герцог",
      "Ткач",
      "Шериф",
      "Пастух",
      "Лакей",
      "Писарь",
      "Кузнец",
      "Пекарь",
      "Конюх",
      "Купец",
      "Боярин",
      "Дьяк",
      "Барсук",
      "Гусь",
      "Хорёк",
      "Мул",
      "Козёл",
      "Кабан",
      "Ворон",
      "Крот",
      "Ёж",
      "Петух",
      "Осёл",
      "Пёс",
      "Морж",
      "Тупик",
      "Филин",
      "Индюк",
      "Пудинг",
      "Кекс",
      "Огурец",
      "Сухарь",
      "Пирог",
      "Кисель",
      "Чайник",
      "Черпак",
      "Горшок",
      "Лапоть",
      "Колпак",
    ],
    feminine: [
      "Жаба",
      "Ласка",
      "Ворона",
      "Выдра",
      "Сорока",
      "Цапля",
      "Улитка",
      "Репа",
      "Каша",
      "Брюква",
      "Тыква",
      "Бадья",
      "Бочка",
      "Метла",
      "Ложка",
    ],
  },
  uk: {
    moodFirst: true,
    moods: [
      "Бурчливий|Бурчлива",
      "Надутий|Надута",
      "Дрібний|Дрібна",
      "Солоний|Солона",
      "Жовчний|Жовчна",
      "Сварливий|Сварлива",
      "Вразливий|Вразлива",
      "Ображений|Ображена",
      "Злющий|Злюща",
      "Нервовий|Нервова",
      "Понурий|Понура",
      "Кислий|Кисла",
      "Уїдливий|Уїдлива",
      "Колючий|Колюча",
      "Чубатий|Чубата",
      "Похмурий|Похмура",
      "Сумний|Сумна",
      "Хмурий|Хмура",
      "Дражливий|Дражлива",
      "Злий|Зла",
      "Скажений|Скажена",
      "Сердитий|Сердита",
      "Шкідливий|Шкідлива",
      "Їдкий|Їдка",
      "Упертий|Уперта",
      "Гнівний|Гнівна",
      "Вусатий|Вусата",
      "Пихатий|Пихата",
      "Чванливий|Чванлива",
      "Поважний|Поважна",
      "Спритний|Спритна",
      "Слизький|Слизька",
      "Лукавий|Лукава",
      "Хитрий|Хитра",
      "Жвавий|Жвава",
      "Лютий|Люта",
      "Сміливий|Смілива",
      "Галантний|Галантна",
      "Хоробрий|Хоробра",
      "Дужий|Дужа",
      "Завзятий|Завзята",
      "Суворий|Сувора",
      "Строгий|Строга",
      "Буйний|Буйна",
      "Шалений|Шалена",
      "Веселий|Весела",
      "Зухвалий|Зухвала",
      "Вогняний|Вогняна",
      "Мокрий|Мокра",
      "Брудний|Брудна",
      "Іржавий|Іржава",
      "Замшілий|Замшіла",
      "Кудлатий|Кудлата",
      "Хиткий|Хитка",
      "Кволий|Квола",
      "Сонний|Сонна",
      "Жадібний|Жадібна",
      "Скупий|Скупа",
      "Нещасний|Нещасна",
      "Лихий|Лиха",
      "Могутній|Могутня",
      "Плечистий|Плечиста",
      "Грубий|Груба",
      "Бравий|Брава",
    ],
    masculine: [
      "Барон",
      "Сквайр",
      "Ченець",
      "Штукар",
      "Швець",
      "Лицар",
      "Бард",
      "Абат",
      "Герцог",
      "Шериф",
      "Пастух",
      "Писар",
      "Коваль",
      "Пекар",
      "Конюх",
      "Ткач",
      "Митар",
      "Купець",
      "Боярин",
      "Козак",
      "Кобзар",
      "Чумак",
      "Борсук",
      "Гусак",
      "Тхір",
      "Цап",
      "Кабан",
      "Ворон",
      "Кріт",
      "Їжак",
      "Півень",
      "Віслюк",
      "Пес",
      "Кіт",
      "Пугач",
      "Бобер",
      "Морж",
      "Сом",
      "Пиріг",
      "Бублик",
      "Кисіль",
      "Огірок",
      "Сухар",
      "Чайник",
      "Казан",
      "Горщик",
      "Ковпак",
      "Чобіт",
      "Буряк",
      "Борщ",
      "Кожух",
      "Глечик",
    ],
    feminine: [
      "Жаба",
      "Коза",
      "Ворона",
      "Сорока",
      "Чапля",
      "Видра",
      "Ріпа",
      "Галушка",
      "Капуста",
      "Макітра",
      "Мітла",
      "Торба",
    ],
  },
  es: {
    moodFirst: false,
    moods: [
      "Gruñón|Gruñona",
      "Mohíno|Mohína",
      "Mezquino|Mezquina",
      "Salado|Salada",
      "Amargado|Amargada",
      "Rezongón|Rezongona",
      "Picajoso|Picajosa",
      "Ofendido|Ofendida",
      "Molesto|Molesta",
      "Irritable",
      "Huraño|Huraña",
      "Agrio|Agria",
      "Seco|Seca",
      "Espinoso|Espinosa",
      "Voluble",
      "Sombrío|Sombría",
      "Mustio|Mustia",
      "Adusto|Adusta",
      "Sensible",
      "Rabioso|Rabiosa",
      "Furioso|Furiosa",
      "Rencoroso|Rencorosa",
      "Arisco|Arisca",
      "Quejoso|Quejosa",
      "Terco|Terca",
      "Quejón|Quejona",
      "Ufano|Ufana",
      "Estirado|Estirada",
      "Altivo|Altiva",
      "Pomposo|Pomposa",
      "Taimado|Taimada",
      "Ladino|Ladina",
      "Astuto|Astuta",
      "Pillo|Pilla",
      "Bravo|Brava",
      "Feroz",
      "Osado|Osada",
      "Gallardo|Gallarda",
      "Valiente",
      "Recio|Recia",
      "Audaz",
      "Torvo|Torva",
      "Severo|Severa",
      "Bronco|Bronca",
      "Revoltoso|Revoltosa",
      "Jovial",
      "Pícaro|Pícara",
      "Fogoso|Fogosa",
      "Empapado|Empapada",
      "Embarrado|Embarrada",
      "Oxidado|Oxidada",
      "Mohoso|Mohosa",
      "Greñudo|Greñuda",
      "Flojo|Floja",
      "Torpe",
      "Dormilón|Dormilona",
      "Codicioso|Codiciosa",
      "Tacaño|Tacaña",
      "Infeliz",
      "Apuesto|Apuesta",
      "Poderoso|Poderosa",
      "Fornido|Fornida",
      "Hosco|Hosca",
      "Esforzado|Esforzada",
    ],
    masculine: [
      "Barón",
      "Fraile",
      "Bufón",
      "Juglar",
      "Bardo",
      "Monje",
      "Abad",
      "Duque",
      "Paje",
      "Pastor",
      "Sastre",
      "Obispo",
      "Conde",
      "Mozo",
      "Lacayo",
      "Cura",
      "Hereje",
      "Rufián",
      "Truhán",
      "Bribón",
      "Prior",
      "Siervo",
      "Tejón",
      "Ganso",
      "Sapo",
      "Hurón",
      "Mulo",
      "Chivo",
      "Jabalí",
      "Cuervo",
      "Topo",
      "Erizo",
      "Gallo",
      "Burro",
      "Gato",
      "Búho",
      "Pato",
      "Cerdo",
      "Castor",
      "Nabo",
      "Rábano",
      "Pepino",
      "Puerro",
      "Turrón",
      "Queso",
      "Yunque",
      "Barril",
      "Botijo",
      "Churro",
    ],
    feminine: [
      "Cabra",
      "Rana",
      "Urraca",
      "Garza",
      "Nutria",
      "Morsa",
      "Oca",
      "Babosa",
      "Berza",
      "Patata",
      "Olla",
      "Sartén",
      "Escoba",
      "Bruja",
      "Tetera",
    ],
  },
};
